import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { Store } from "../utils/storage";
import { getQuery, post } from "../utils/api";
import { getValidDate } from "../utils/formatting";

const storage = Store("session");

const State = {
  searchText: "",
  filteredEvents: [],
  featuredEvents: [],
  categories: [],
  selectedEvent: {},
  selectedCategories: [],
  selectedFromDate: "",
  selectedToDate: "",
  searchResults: [],
  nextPage: null,
  hasMore: false,
  initialLoading: false,
  loading: false,
  loadingCategories: false,
  totalFound: null,
  overrideVerify: false,
};

const buildPayload = ({ text, fromDate, toDate, categories = [], page, includeDates }) => {
  const payload = {};
  payload.pageSize = 20; // Set a default page size
  if (text) payload.searchString = text.toLowerCase().trim();
  if (includeDates) {
    payload.fromDate = getValidDate(fromDate).toISOString();
    payload.toDate = getValidDate(toDate).toISOString();
  }
  if (categories?.length > 0) payload.categories = categories;
  if (page) payload.page = page;

  return payload;
};

const handleEventsResponse = (response, set, override = {}) => {
  const eventsData = response?.data;
  if (response.response === 200 && eventsData) {
    const pagination = eventsData.pagination || {};
    const items = eventsData.events || [];
    const nextPage = pagination.next_page || null;
    const hasMore = !!nextPage;
    const totalFound = pagination.total_records || 0;
    set({ searchResults: items, filteredEvents: items, nextPage, hasMore, totalFound, ...override });
  } else {
    console.error("Event fetch failed:", response.error || response);
    set({ searchResults: [], filteredEvents: [], nextPage: null, hasMore: false, totalFound: 0, ...override });
  }
};

const Actions = (_set, get) => {
  const set = _set;
  const URL = process.env.REACT_APP_API_ENDPOINT;

  const setText = async (text) => {
    const { selectedCategories, selectedFromDate, selectedToDate } = get();

    set({
      searchText: text,
      initialLoading: true,
    });

    try {
      const payload = buildPayload({
        text,
        fromDate: selectedFromDate,
        toDate: selectedToDate,
        categories: selectedCategories,
        includeDates: !!(selectedFromDate && selectedToDate),
      });
      const response = await post(`${URL}/events`, payload);
      handleEventsResponse(response, set, { initialLoading: false });
    } catch (error) {
      console.error("setText error:", error);
      set({ searchResults: [], filteredEvents: [], nextPage: null, hasMore: false, initialLoading: false });
    }
  };

  const fetchMoreSearchResults = async () => {
    const {
      searchText,
      nextPage,
      searchResults,
      filteredEvents,
      loading,
      hasMore,
      selectedCategories,
      selectedFromDate,
      selectedToDate,
    } = get();
    if (!nextPage || !hasMore || loading) return;
    set({ loading: true });

    try {
      const payload = buildPayload({
        text: searchText,
        fromDate: selectedFromDate,
        toDate: selectedToDate,
        categories: selectedCategories,
        page: nextPage,
        includeDates: !!(selectedFromDate && selectedToDate),
      });
      const response = await post(`${URL}/events`, payload);
      const eventsData = response?.data;

      if (response.response === 200 && eventsData) {
        const pagination = eventsData.pagination || {};
        const items = eventsData.events || [];
        const newNextPage = pagination.next_page || null;
        const hasMore = !!newNextPage;
        set({
          searchResults: [...searchResults, ...items],
          filteredEvents: [...filteredEvents, ...items],
          nextPage: newNextPage,
          hasMore: hasMore,
          loading: false,
        });
      } else {
        console.error("Fetch more failed:", response.error || response);
        set({ loading: false });
      }
    } catch (error) {
      console.error("fetchMoreSearchResults error:", error);
      set({ loading: false });
    }
  };

  const clear = async () => {
    set({
      searchText: "",
      searchResults: [],
      filteredEvents: [],
      nextPage: null,
    });
    await applyFilters();
  };

  const fetchCategories = async () => {
    set({ loadingCategories: true });
    const response = await getQuery(`${URL}/categories`);
    if (response.response === 200) {
      set({ categories: response.data.getCategoriesV2 || [], loadingCategories: false });
    } else {
      console.error("fetchCategories error:", response.error || response);
      set({ loadingCategories: false });
    }
  };

  const applyFilters = async () => {
    const { selectedFromDate, selectedToDate, selectedCategories, searchText } = get();

    let fromDateCheck = selectedFromDate;
    let toDateCheck = selectedToDate;

    if (selectedFromDate || selectedToDate) {
      const fromDate = new Date(selectedFromDate).getTime();
      const toDate = new Date(selectedToDate).getTime();

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateTodayFormatted = today.getTime();

      if (fromDate < dateTodayFormatted || toDate < dateTodayFormatted) {
        fromDateCheck = null;
        toDateCheck = null;
      }
    }

    set({ initialLoading: true, selectedFromDate: fromDateCheck, selectedToDate: toDateCheck });

    const includeDates = !!(selectedFromDate && selectedToDate);

    try {
      const payload = buildPayload({
        text: searchText,
        fromDate: fromDateCheck,
        toDate: toDateCheck,
        categories: selectedCategories,
        includeDates,
      });

      const response = await post(`${URL}/events`, payload);
      handleEventsResponse(response, set, { initialLoading: false });
    } catch (error) {
      console.error("applyFilters error:", error);
      set({ searchResults: [], filteredEvents: [], nextPage: null, hasMore: false, initialLoading: false });
    }
  };

  const fetchEventByKey = async (eventKey) => {
    const URL = process.env.REACT_APP_API_ENDPOINT;
    set({ initialLoading: true });

    try {
      const response = await getQuery(`${URL}/event-details/?eventOccurrenceId=${eventKey}`);
      const eventData = response?.data?.getEventDetails;
      if (response.response === 200 && eventData) {
        set({
          selectedEvent: { ...eventData.item, occurrence_id: eventKey },
          initialLoading: false,
        });
      } else {
        console.error("Failed to fetch event details:", response.error || response);
        set({
          selectedEvent: {},
          initialLoading: false,
        });
      }
    } catch (error) {
      console.error("Error in fetchEventByKey:", error);
      set({
        selectedEvent: {},
        initialLoading: false,
      });
    }
  };

  const fetchFeaturedEvents = async () => {
    set({ loading: true });

    try {
      const response = await getQuery(`${URL}/featured-events`);
      if (response.response === 200) {
        // Transform REST response to match expected format
        const transformedEvents = (response.data.events || []).map((event) => {
          // Handle image URLs - API returns them as an array with fragments that need to be joined
          // URLs may be split by commas in query parameters (e.g., w_1024,h_1024,c_limit)
          const imageUrls = [];
          if (event.images && Array.isArray(event.images)) {
            let currentUrl = "";

            for (let i = 0; i < event.images.length; i++) {
              const part = String(event.images[i]).trim();

              // If this part starts with http, it's a new URL
              if (part.startsWith("http://") || part.startsWith("https://")) {
                // Save the previous URL if it exists
                if (currentUrl) {
                  imageUrls.push(currentUrl);
                }
                // Start building the new URL
                currentUrl = part;
              } else {
                // This is a continuation of the current URL (query parameter)
                currentUrl += "," + part;
              }
            }

            // Don't forget the last URL
            if (currentUrl) {
              imageUrls.push(currentUrl);
            }
          }

          return {
            occurrence_id: event.occurrence_id,
            name: event.name,
            description: event.description,
            source: event.source,
            categories: event.categories || [],
            categoriesV2: event.categoriesV2 || [],
            images: imageUrls,
            isTaxiAvailable: event.is_taxi_available,
            location: event.location,
            occurrences: event.occurrences || [
              {
                start: event.occurrence_start,
                end: event.occurrence_end,
                url: event.occurrence_url,
                formatted: {
                  startDate: event.occurrences?.[0]?.formatted?.startDate || "",
                },
              },
            ],
          };
        });
        set({ featuredEvents: transformedEvents, loading: false });
      } else {
        console.error("fetchFeaturedEvents error:", response.error || response);
        set({ featuredEvents: [], loading: false });
      }
    } catch (error) {
      console.error("Error in fetchFeaturedEvents:", error);
      set({ featuredEvents: [], loading: false });
    }
  };

  const resetVerify = (state) => {
    set({ overrideVerify: state });
  };

  const setFilterDates = async (fromDate, toDate) => {
    if (toDate < fromDate) {
      toDate = fromDate;
    }

    set({ selectedFromDate: fromDate, selectedToDate: toDate });
    await applyFilters();
  };

  return {
    setText,
    clear,
    reset: () => set(State),
    fetchCategories,
    applyFilters,
    setSelectedFromDate: (date) => set({ selectedFromDate: new Date(date) }),
    setSelectedToDate: (date) => set({ selectedToDate: new Date(date) }),
    fetchMoreSearchResults,
    fetchEventByKey,
    fetchFeaturedEvents,
    resetVerify,
    setFilterDates,
  };
};

const EventStore = create(
  persist(
    (set, get) => {
      const actions = Actions(set, get);

      return {
        ...State,
        setSearchText: actions.setText,
        clearText: actions.clear,
        reset: actions.reset,
        fetchCategories: actions.fetchCategories,
        applyFilters: actions.applyFilters,
        setSelectedFromDate: actions.setSelectedFromDate,
        setSelectedToDate: actions.setSelectedToDate,
        fetchMoreSearchResults: actions.fetchMoreSearchResults,
        fetchEventByKey: actions.fetchEventByKey,
        fetchFeaturedEvents: actions.fetchFeaturedEvents,
        resetVerify: actions.resetVerify,
        setFilterDates: actions.setFilterDates,
      };
    },
    {
      name: "app.events",
      storage: createJSONStorage(() => storage),
      partialize: (state) => {
        const { hasMore, loading, initialLoading, loadingCategories, nextPage, ...persisted } = state;
        return persisted;
      },
    }
  )
);

export default EventStore;
